import { useMutation, useQueryClient } from '@tanstack/react-query';
import ContractorService, {
  ICreateContractorParams,
} from '@clickersphere/core/services/ContractorService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { ContractorQueryData, CONTRACTORS_QUERY_KEY } from './useContractors';

export function useCreateContractor() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: createContractorIsPending,
    mutateAsync: createContractorAsync,
  } = useMutation({
    mutationFn: async (data: ICreateContractorParams) => {
      const contractor = await ContractorService.create(data);
      return contractor as ContractorQueryData;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACTORS_QUERY_KEY),
      });

      const tempId = String(crypto.randomUUID());

      queryClient.setQueryData<ContractorQueryData[]>(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
        (old) => [
          ...(old ?? []),
          {
            id: tempId,
            tenantId: '',
            personType: variables.personType,
            name: variables.name,
            documentNumber: variables.documentNumber,
            legalRepresentativeName: variables.legalRepresentativeName,
            legalRepresentativeDocument: variables.legalRepresentativeDocument,
            email: variables.email,
            phone: variables.phone,
            instagram: variables.instagram,
            address: variables.address ?? null,
            createdAt: new Date().toISOString(),
            deletedAt: null,
            queryStatus: 'pending',
          },
        ],
      );

      return { tempId };
    },
    onSuccess: async (data, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACTORS_QUERY_KEY),
      });

      queryClient.setQueryData<ContractorQueryData[]>(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
        (old) =>
          old?.map((contractor) =>
            contractor.id === context.tempId ? data : contractor,
          ),
      );
    },
    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACTORS_QUERY_KEY),
      });

      queryClient.setQueryData<ContractorQueryData[]>(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
        (old) => old?.filter((contractor) => contractor.id !== context?.tempId),
      );
    },
  });

  return {
    createContractorAsync,
    createContractorIsPending,
  };
}
